import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import {
  ListPager,
  STAFF_LIST_PAGE_SIZE,
  totalDescription,
  useCursorPages,
  type PagedResponse,
} from '@/components/templates/list-pager'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import type { MembershipLevel, MembershipLevelsResponse } from '@/pages/MembershipLevelsPage.types'

type OrganizationMembershipStatus =
  | 'Active'
  | 'Lapsed'
  | 'Suspended'
  | 'Pending-New'
  | 'Pending-Renewal'
type Sort = 'name' | 'renewal_due' | 'roster_count'
type Direction = 'ascending' | 'descending'

interface OrganizationMembership {
  id: number
  organization_id: number
  organization_name: string
  level: string
  status: OrganizationMembershipStatus
  renewal_due_on: string | null
  roster_count: number
}

interface OrganizationMembershipsResponse extends PagedResponse {
  organization_memberships: OrganizationMembership[]
}

const statusVariant: Record<OrganizationMembershipStatus, BadgeProps['variant']> = {
  Active: 'success',
  Lapsed: 'warning',
  Suspended: 'neutral',
  'Pending-New': 'warning',
  'Pending-Renewal': 'warning',
}

export function OrganizationMembershipsPage() {
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState('all')
  const [levelId, setLevelId] = React.useState('all')
  const [sort, setSort] = React.useState<Sort>('name')
  const [direction, setDirection] = React.useState<Direction>('ascending')
  const levelsQuery = useQuery({
    queryKey: ['membership-levels', 'organization-memberships-filter'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const params = new URLSearchParams({ sort, direction })
  if (search) params.set('search', search)
  if (status !== 'all') params.set('status', status)
  if (levelId !== 'all') params.set('membership_level_id', levelId)
  const { query: membershipsQuery, pager } = useCursorPages<OrganizationMembershipsResponse>({
    queryKey: ['organization-memberships'],
    path: `/api/v1/organization_memberships?${params.toString()}`,
    limit: STAFF_LIST_PAGE_SIZE,
  })
  const toggleSort = (next: Sort) => {
    if (sort === next)
      setDirection((current) => (current === 'ascending' ? 'descending' : 'ascending'))
    else {
      setSort(next)
      setDirection('ascending')
    }
  }
  const hasFilters = Boolean(search) || status !== 'all' || levelId !== 'all'

  return (
    <ListPage
      title="Organization memberships"
      description={totalDescription(
        membershipsQuery.data?.total_count,
        'Organization membership',
        'Organization memberships',
        hasFilters,
      )}
      search={{ value: search, onChange: setSearch, placeholder: 'Search organizations…' }}
      toolbar={
        <>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger aria-label="Status" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(['Active', 'Pending-New', 'Pending-Renewal', 'Lapsed', 'Suspended'] as const).map(
                (value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
          <Select value={levelId} onValueChange={setLevelId}>
            <SelectTrigger aria-label="Level" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              {levelsQuery.data?.membership_levels
                .filter((level) => level.kind === 'organization')
                .map((level: MembershipLevel) => (
                  <SelectItem key={level.id} value={String(level.id)}>
                    {level.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </>
      }
      table={{
        rows: membershipsQuery.data?.organization_memberships ?? [],
        columns: columns(sort, direction, toggleSort),
        getRowId: (membership) => membership.id,
        getRowLabel: (membership) => membership.organization_name,
        state: membershipsQuery.isLoading
          ? 'loading'
          : membershipsQuery.isError
            ? 'error'
            : 'ready',
        loadingTitle: 'Loading Organization Memberships',
        emptyTitle: hasFilters
          ? 'No matching Organization Memberships'
          : 'No Organization Memberships yet',
        errorTitle: 'Organization Memberships unavailable',
        header: <ListPager pager={pager} label="Organization memberships" position="top" />,
        footer: <ListPager pager={pager} label="Organization memberships" position="bottom" />,
      }}
    />
  )
}

function columns(
  sort: Sort,
  direction: Direction,
  toggleSort: (sort: Sort) => void,
): ListPageColumn<OrganizationMembership>[] {
  const sortable = (id: Sort) => ({
    active: sort === id,
    direction,
    onToggle: () => toggleSort(id),
  })
  return [
    {
      id: 'name',
      header: 'Organization',
      sort: sortable('name'),
      cell: (membership) => (
        <Button variant="link" size="inline" asChild className="font-normal">
          <a href={`/contacts/organizations/${membership.organization_id}`}>
            {membership.organization_name}
          </a>
        </Button>
      ),
    },
    {
      id: 'level',
      header: 'Level',
      cell: (membership) => <span className="text-muted-foreground">{membership.level}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (membership) => (
        <Badge variant={statusVariant[membership.status]}>{membership.status}</Badge>
      ),
    },
    {
      id: 'renewal',
      header: 'Renewal due',
      sort: sortable('renewal_due'),
      cell: (membership) => displayDate(membership.renewal_due_on),
    },
    {
      id: 'roster',
      header: 'Roster',
      sort: sortable('roster_count'),
      cell: (membership) => membership.roster_count,
    },
  ]
}
